// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — DashboardPage
// Assembles KPIs, charts, branch performance, with drill-down navigation
// ══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { BarChart3, FileText } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { PageHeader } from '@components/ui/PageHeader';
import { Grid } from '@components/ui/Primitives';
import { EmptyState } from '@components/ui/States';
import { KpiGrid } from '@components/dashboard/KpiGrid';
import { RevenueTrendChart } from '@components/dashboard/RevenueTrendChart';
import { TopProductsChart } from '@components/dashboard/TopProductsChart';
import { BranchPerformanceCards } from '@components/dashboard/BranchPerformanceCards';
import { WasteDrillDown } from '@components/dashboard/WasteDrillDown';
import { ProfitDrillDown } from '@components/dashboard/ProfitDrillDown';

export function DashboardPage({ data, refresh, navigate }) {
  const { theme } = useTheme();
  const { t } = useLang();
  const [drillView, setDrillView] = useState(null);

  // Empty state (tenant with no data)
  if (!data?.sales || data.sales.length === 0) {
    return (
      <EmptyState
        icon={<FileText size={36} color={theme.primary} />}
        title={t.noDataYet}
        description={t.noDataDesc}
      />
    );
  }

  // Drill-down views
  if (drillView === 'waste') {
    return <WasteDrillDown data={data} onBack={() => setDrillView(null)} />;
  }
  if (drillView === 'profit') {
    return <ProfitDrillDown data={data} onBack={() => setDrillView(null)} />;
  }

  return (
    <div>
      <PageHeader
        icon={<BarChart3 size={22} color={theme.primary} />}
        title={t.businessOverview}
        description={t.last30Days}
      />

      <div style={{ marginBottom: 24 }}>
        <KpiGrid data={data} onDrillDown={setDrillView} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <Grid min={380}>
          <RevenueTrendChart sales={data.sales} />
          <TopProductsChart products={data.products} />
        </Grid>
      </div>

      <BranchPerformanceCards
        branches={data.branches}
        sales={data.sales}
      />
    </div>
  );
}
